// My Call Queue — sales reps' daily call list.
// Phone outreach is CBC Sales's primary motion (marketing owns email).
// Click-to-dial uses Zoom Phone's URI scheme, with tel: fallback.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { BulkModal } from "@/components/bulk-modal";
import { Phone, PhoneCall, Clock, SkipForward, CheckCircle2, VoicemailIcon, PhoneOff, PhoneMissed, CircleSlash, UserX, EyeOff, RotateCcw, Settings2 } from "lucide-react";

type Kind = "lead" | "contact";
type Disposition =
  | "CONNECTED" | "VOICEMAIL" | "NO_ANSWER" | "BUSY"
  | "GATEKEEPER" | "WRONG_NUMBER" | "NOT_INTERESTED"
  | "CALLBACK" | "DO_NOT_CALL";

interface QueueRow {
  kind: Kind;
  id: string;
  name: string;
  title: string | null;
  company: string | null;
  accountId: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  score: number | null;
  status: string | null;
  source: string | null;
  callAttempts: number;
  lastCallAt: string | null;
  lastCallOutcome: Disposition | null;
  nextCallbackAt: string | null;
  bucket: 0 | 1 | 2;  // 0 due callback, 1 untouched, 2 follow-up
}

const DISPOSITIONS: { value: Disposition; label: string; icon: JSX.Element; tone?: "success" | "warn" | "danger" }[] = [
  { value: "CONNECTED",      label: "Connected",       icon: <CheckCircle2 className="h-4 w-4" />, tone: "success" },
  { value: "VOICEMAIL",      label: "Left voicemail",  icon: <VoicemailIcon className="h-4 w-4" /> },
  { value: "NO_ANSWER",      label: "No answer",       icon: <PhoneMissed className="h-4 w-4" /> },
  { value: "BUSY",           label: "Busy",            icon: <PhoneOff className="h-4 w-4" /> },
  { value: "GATEKEEPER",     label: "Gatekeeper",      icon: <UserX className="h-4 w-4" /> },
  { value: "CALLBACK",       label: "Schedule callback", icon: <Clock className="h-4 w-4" />, tone: "warn" },
  { value: "WRONG_NUMBER",   label: "Wrong number",    icon: <CircleSlash className="h-4 w-4" /> },
  { value: "NOT_INTERESTED", label: "Not interested",  icon: <PhoneOff className="h-4 w-4" /> },
  { value: "DO_NOT_CALL",    label: "Do not call",     icon: <UserX className="h-4 w-4" />, tone: "danger" },
];

function normalizePhone(s: string | null | undefined): string | null {
  if (!s) return null;
  return s.replace(/[^0-9+]/g, "");
}

// Zoom Phone click-to-dial — falls back to tel: if Zoom isn't installed.
function dial(phone: string | null | undefined) {
  const p = normalizePhone(phone);
  if (!p) return;
  // Primary: open Zoom Phone dialer
  window.location.href = `zoomphonecall://${p}`;
  // Fallback after a short delay (browsers don't redirect if the URI scheme is registered, so tel: only fires if zoom isn't handling it)
  setTimeout(() => {
    window.location.href = `tel:${p}`;
  }, 500);
}

function bucketLabel(b: 0 | 1 | 2) {
  return b === 0 ? "Due now" : b === 1 ? "Fresh" : "Follow-up";
}
function bucketTone(b: 0 | 1 | 2) {
  return b === 0 ? "default" : b === 1 ? "secondary" : "outline";
}

function fmtRel(iso: string | null) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const d = Math.round(diff / 86_400_000);
  if (Math.abs(d) < 1) {
    const h = Math.round(diff / 3_600_000);
    return h >= 0 ? `${h}h ago` : `in ${Math.abs(h)}h`;
  }
  return d >= 0 ? `${d}d ago` : `in ${Math.abs(d)}d`;
}

type Cadence = {
  maxAttempts: number; redialConnected: number; redialVoicemail: number; redialNoAnswer: number;
  redialBusy: number; redialGatekeeper: number; redialNotInterested: number; redialWrongNumber: number;
};
const CADENCE_FIELDS: { key: keyof Cadence; label: string }[] = [
  { key: "maxAttempts",         label: "Max attempts before dropping" },
  { key: "redialConnected",     label: "After connected — retry (hrs)" },
  { key: "redialVoicemail",     label: "After voicemail — retry (hrs)" },
  { key: "redialNoAnswer",      label: "After no answer — retry (hrs)" },
  { key: "redialBusy",          label: "After busy — retry (hrs)" },
  { key: "redialGatekeeper",    label: "After gatekeeper — retry (hrs)" },
  { key: "redialNotInterested", label: "After not interested — retry (hrs)" },
  { key: "redialWrongNumber",   label: "After wrong number — retry (hrs)" },
];

export default function CallQueuePage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [bucketFilter, setBucketFilter] = useState<"all" | "due" | "fresh" | "followup">("all");
  const [view, setView] = useState<"active" | "removed">("active");
  const [active, setActive] = useState<QueueRow | null>(null);
  const [disposition, setDisposition] = useState<Disposition>("NO_ANSWER");
  const [callbackAt, setCallbackAt] = useState("");
  const [notes, setNotes] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [cfg, setCfg] = useState<Cadence | null>(null);

  async function openConfig() {
    const c = (await api.get("/call-queue/config")).data as Cadence;
    setCfg({
      maxAttempts: c.maxAttempts,
      redialConnected: c.redialConnected,
      redialVoicemail: c.redialVoicemail,
      redialNoAnswer: c.redialNoAnswer,
      redialBusy: c.redialBusy,
      redialGatekeeper: c.redialGatekeeper,
      redialNotInterested: c.redialNotInterested,
      redialWrongNumber: c.redialWrongNumber,
    });
    setShowConfig(true);
  }

  const saveCfg = useMutation({
    mutationFn: async () => api.put("/call-queue/config", cfg),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/call-queue"] }); setShowConfig(false); },
  });
  const resetCfg = useMutation({
    mutationFn: async () => api.delete("/call-queue/config"),
    onSuccess: async () => {
      const c = (await api.get("/call-queue/config")).data as Cadence;
      setCfg(c);
      qc.invalidateQueries({ queryKey: ["/call-queue"] });
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["/call-queue", view],
    queryFn: async () => (await api.get("/call-queue", { params: { view } })).data as { data: QueueRow[]; stats: { total: number; dueCallbacks: number; untouched: number; followUps: number } },
    refetchOnMount: true,
  });

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    const byBucket = all.filter((r) =>
      bucketFilter === "all" ? true :
      bucketFilter === "due" ? r.bucket === 0 :
      bucketFilter === "fresh" ? r.bucket === 1 :
      r.bucket === 2,
    );
    if (!q.trim()) return byBucket;
    const needle = q.toLowerCase();
    return byBucket.filter((r) =>
      r.name.toLowerCase().includes(needle) ||
      (r.company ?? "").toLowerCase().includes(needle) ||
      (r.phone ?? "").includes(needle),
    );
  }, [data, q, bucketFilter]);

  const stats = data?.stats;

  const logMut = useMutation({
    mutationFn: async () => {
      if (!active) return;
      const payload: any = {
        kind: active.kind,
        id: active.id,
        disposition,
        notes: notes || undefined,
        durationSec: durationMin ? Math.round(parseFloat(durationMin) * 60) : undefined,
      };
      if (disposition === "CALLBACK") {
        if (!callbackAt) throw new Error("Pick a callback date/time.");
        payload.callbackAt = new Date(callbackAt).toISOString();
      }
      await api.post("/call-queue/log", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/call-queue"] });
      qc.invalidateQueries({ queryKey: ["/activities"] });
      setActive(null);
      setNotes(""); setDurationMin(""); setCallbackAt(""); setDisposition("NO_ANSWER");
    },
  });

  async function skip(r: QueueRow, hours = 24) {
    await api.post("/call-queue/skip", { kind: r.kind, id: r.id, hours });
    qc.invalidateQueries({ queryKey: ["/call-queue"] });
  }

  async function removeFromQueue(r: QueueRow) {
    if (!confirm(`Remove ${r.name} from the call queue? You can restore them from the Removed tab.`)) return;
    await api.post("/call-queue/remove", { kind: r.kind, id: r.id });
    qc.invalidateQueries({ queryKey: ["/call-queue"] });
  }

  async function addBack(r: QueueRow) {
    await api.post("/call-queue/add-back", { kind: r.kind, id: r.id });
    qc.invalidateQueries({ queryKey: ["/call-queue"] });
  }

  function openDisposition(r: QueueRow) {
    setActive(r);
    setDisposition("NO_ANSWER");
    setNotes(""); setDurationMin(""); setCallbackAt("");
  }

  return (
    <>
      <PageHeader
        title="My Call Queue"
        description="Phone outreach to your owned leads + contacts, prioritized. Dials through Zoom Phone."
        actions={<Button variant="outline" size="sm" onClick={openConfig}><Settings2 className="mr-1 h-4 w-4" />Cadence settings</Button>}
      />

      <div className="p-6 space-y-4">
        {/* Active / Removed tabs */}
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setView("active")}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${view === "active" ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            Active queue
          </button>
          <button
            onClick={() => setView("removed")}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${view === "removed" ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            Removed
          </button>
        </div>

        {/* Stats strip */}
        <div className="grid gap-3 md:grid-cols-4">
          <StatCard label="Total in queue"   value={stats?.total ?? "—"} />
          <StatCard label="Due callbacks"    value={stats?.dueCallbacks ?? "—"} active={bucketFilter === "due"} onClick={() => setBucketFilter(bucketFilter === "due" ? "all" : "due")} />
          <StatCard label="Fresh (untouched)" value={stats?.untouched ?? "—"} active={bucketFilter === "fresh"} onClick={() => setBucketFilter(bucketFilter === "fresh" ? "all" : "fresh")} />
          <StatCard label="Follow-ups"       value={stats?.followUps ?? "—"} active={bucketFilter === "followup"} onClick={() => setBucketFilter(bucketFilter === "followup" ? "all" : "followup")} />
        </div>

        <div className="flex items-center gap-3">
          <Input
            placeholder="Search name / company / phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 max-w-sm"
          />
          {bucketFilter !== "all" && (
            <Button size="sm" variant="ghost" onClick={() => setBucketFilter("all")}>Clear filter</Button>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            {isLoading && <p className="p-8 text-center text-sm text-muted-foreground">Loading your queue…</p>}
            {!isLoading && rows.length === 0 && (
              <p className="p-10 text-center text-sm text-muted-foreground">
                {view === "removed" ? (
                  <>Nothing removed from your queue yet. When you hit "Remove from queue" on an active row, it shows up here.</>
                ) : (
                  <>No one to call right now. Assign yourself leads on <Link to="/leads" className="text-primary hover:underline">the Leads page</Link> or wait for callbacks to come due.</>
                )}
              </p>
            )}
            <ul className="divide-y">
              {rows.map((r) => (
                <li key={`${r.kind}-${r.id}`} className="grid items-center gap-3 px-4 py-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto_auto] text-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={r.kind === "lead" ? `/leads` /* lead detail route not yet built */ : `/contacts/${r.id}`}
                        className="truncate font-medium hover:underline"
                      >
                        {r.name}
                      </Link>
                      <Badge variant={bucketTone(r.bucket) as any} className="text-[10px]">{bucketLabel(r.bucket)}</Badge>
                      <Badge variant="outline" className="text-[10px]">{r.kind}</Badge>
                      {r.score ? <span className="text-xs text-muted-foreground">score {r.score}</span> : null}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {r.title ? `${r.title} · ` : ""}{r.company ?? "—"}
                    </div>
                  </div>

                  <div className="min-w-0 text-xs text-muted-foreground">
                    <div>{r.phone ?? r.mobile ?? <span className="italic">no phone</span>}</div>
                    <div>
                      {r.lastCallAt ? `Last: ${fmtRel(r.lastCallAt)} · ${r.lastCallOutcome?.replace(/_/g, " ")}` : "Never called"}
                      {r.callAttempts > 0 && <> · attempt {r.callAttempts}</>}
                    </div>
                  </div>

                  <Button
                    onClick={() => dial(r.phone ?? r.mobile)}
                    disabled={!r.phone && !r.mobile || view === "removed"}
                    className="gap-1"
                  >
                    <PhoneCall className="h-4 w-4" />Call via Zoom
                  </Button>

                  <div className="flex items-center gap-1">
                    {view === "active" ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openDisposition(r)}>Log outcome</Button>
                        <Button size="sm" variant="ghost" onClick={() => skip(r, 24)} title="Skip 24 hours"><SkipForward className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => removeFromQueue(r)} title="Remove from queue"><EyeOff className="h-4 w-4" /></Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => addBack(r)} title="Put back into the queue">
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />Add back
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> Zoom Phone required for click-to-dial.</span>
          {" "}If Zoom isn't installed, your browser will fall back to the default <code>tel:</code> handler.
        </p>
      </div>

      {/* Disposition modal */}
      <BulkModal
        open={!!active}
        onClose={() => setActive(null)}
        title={active ? `Log call — ${active.name}` : "Log call"}
        description={active ? `Attempt ${active.callAttempts + 1}. What happened on this call?` : undefined}
        confirmLabel="Save outcome"
        confirmDisabled={disposition === "CALLBACK" && !callbackAt}
        busy={logMut.isPending}
        onConfirm={() => logMut.mutate()}
        error={logMut.error ? (logMut.error as any).message ?? "Save failed." : null}
      >
        <div className="space-y-3">
          <div>
            <Label>Outcome</Label>
            <div className="mt-1 grid grid-cols-3 gap-1.5">
              {DISPOSITIONS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setDisposition(d.value)}
                  className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition ${
                    disposition === d.value
                      ? "border-primary bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  } ${d.tone === "danger" ? "text-destructive" : ""}`}
                >
                  {d.icon}
                  <span className="truncate">{d.label}</span>
                </button>
              ))}
            </div>
          </div>

          {disposition === "CALLBACK" && (
            <div>
              <Label htmlFor="cb-at">Callback at</Label>
              <Input
                id="cb-at"
                type="datetime-local"
                value={callbackAt}
                onChange={(e) => setCallbackAt(e.target.value)}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cb-dur">Duration (min)</Label>
              <Input id="cb-dur" type="number" min="0" step="0.5" value={durationMin} onChange={(e) => setDurationMin(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cb-status" className="invisible">Status</Label>
              <Select
                id="cb-status"
                value={disposition}
                onChange={(e) => setDisposition(e.target.value as Disposition)}
              >
                {DISPOSITIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="cb-notes">Notes</Label>
            <textarea
              id="cb-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did they say? Any next steps?"
              className="flex w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
        </div>
      </BulkModal>

      {/* Cadence settings */}
      <BulkModal
        open={showConfig}
        onClose={() => setShowConfig(false)}
        title="Call cadence settings"
        description="Hours to wait before a record becomes callable again, per outcome. Applies to your queue only."
        confirmLabel="Save"
        busy={saveCfg.isPending}
        onConfirm={() => saveCfg.mutate()}
        error={saveCfg.error ? "Save failed." : null}
      >
        {cfg && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {CADENCE_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    type="number"
                    min={0}
                    value={cfg[f.key]}
                    onChange={(e) => setCfg({ ...cfg, [f.key]: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                  />
                </div>
              ))}
            </div>
            <button type="button" onClick={() => resetCfg.mutate()} className="text-xs text-muted-foreground underline">
              Reset to system defaults
            </button>
          </div>
        )}
      </BulkModal>
    </>
  );
}

function StatCard({
  label, value, active, onClick,
}: { label: string; value: number | string; active?: boolean; onClick?: () => void }) {
  const Inner = (
    <Card className={active ? "border-primary bg-primary/5" : ""}>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
  return onClick ? <button onClick={onClick} className="text-left">{Inner}</button> : Inner;
}
